import { Timestamp, onSnapshot, orderBy, query, where, type QuerySnapshot } from 'firebase/firestore'
import { create } from 'zustand'
import { DONE_WINDOW_DAYS } from '@/config'
import { taskCollection, type Paths } from '@/data/tasks'
import type { Scope, Task, TaskDoc } from '@/types'

// A small, fixed set of listeners feeds one in-memory store. Per task
// collection, two listeners: open tasks (repeats and routines always are, with
// their full tick history), and one-offs ticked in the last 30 days. The Plan
// tab, the Calendar and search derive from this store; a past week beyond 30
// days loads its one-offs once.

type Bucket = 'sharedOpen' | 'sharedDone' | 'privateOpen' | 'privateDone'

interface TasksState {
  buckets: Record<Bucket, Record<string, Task>>
  arrived: Record<Bucket, boolean>
  /** True once every listener has delivered its first snapshot. */
  ready: boolean
  /** Shared and private tasks as one list. */
  all: Task[]
  byId: Record<string, Task>
}

const emptyBuckets = (): Record<Bucket, Record<string, Task>> => ({ sharedOpen: {}, sharedDone: {}, privateOpen: {}, privateDone: {} })
const noArrivals = (): Record<Bucket, boolean> => ({ sharedOpen: false, sharedDone: false, privateOpen: false, privateDone: false })

export const useTasks = create<TasksState>()(() => ({
  buckets: emptyBuckets(),
  arrived: noArrivals(),
  ready: false,
  all: [],
  byId: {},
}))

function toTask(id: string, data: TaskDoc, scope: Scope, pending: boolean): Task {
  return { ...data, id, scope, pending }
}

function fold(buckets: Record<Bucket, Record<string, Task>>): { all: Task[]; byId: Record<string, Task> } {
  const byId: Record<string, Task> = {}
  for (const b of Object.values(buckets)) for (const t of Object.values(b)) byId[t.id] = t
  return { all: Object.values(byId), byId }
}

export function startTaskListeners(p: Paths): () => void {
  const cutoff = Timestamp.fromDate(new Date(Date.now() - DONE_WINDOW_DAYS * 864e5))

  const apply = (bucket: Bucket, scope: Scope) => (snap: QuerySnapshot) => {
    const next: Record<string, Task> = {}
    snap.forEach((d) => {
      next[d.id] = toTask(d.id, d.data() as TaskDoc, scope, d.metadata.hasPendingWrites)
    })
    useTasks.setState((s) => {
      const buckets = { ...s.buckets, [bucket]: next }
      const arrived = { ...s.arrived, [bucket]: true }
      return { buckets, arrived, ready: Object.values(arrived).every(Boolean), ...fold(buckets) }
    })
  }

  const onError = (bucket: Bucket) => (err: Error) => {
    console.error(`task listener ${bucket}`, err)
    useTasks.setState((s) => {
      const arrived = { ...s.arrived, [bucket]: true }
      return { arrived, ready: Object.values(arrived).every(Boolean) }
    })
  }

  const opts = { includeMetadataChanges: true }
  const stops: (() => void)[] = []
  for (const scope of ['shared', 'private'] as Scope[]) {
    const col = taskCollection(p, scope)
    const open: Bucket = scope === 'shared' ? 'sharedOpen' : 'privateOpen'
    const done: Bucket = scope === 'shared' ? 'sharedDone' : 'privateDone'
    stops.push(onSnapshot(query(col, where('done', '==', false)), opts, apply(open, scope), onError(open)))
    stops.push(
      onSnapshot(
        query(col, where('done', '==', true), where('completedAt', '>=', cutoff), orderBy('completedAt', 'desc')),
        opts,
        apply(done, scope),
        onError(done),
      ),
    )
  }

  return () => {
    stops.forEach((s) => s())
    useTasks.setState({ buckets: emptyBuckets(), arrived: noArrivals(), ready: false, all: [], byId: {} })
  }
}
